export function getStreamingTurnState(message) {
  const transcript =
    typeof message?.transcript === "string" ? message.transcript : "";
  const isEndOfTurn = message?.end_of_turn === true;

  return {
    currentTurnTranscript: transcript,
    partialTranscript: isEndOfTurn ? "" : transcript,
    isEndOfTurn,
    turnOrder: message?.turn_order ?? 0,
  };
}

export function isFormattedFinalTurn(message) {
  return Boolean(
    message?.end_of_turn === true && message?.turn_is_formatted === true
  );
}

export function storeFormattedFinalTurn(
  turnsByOrder,
  message,
  clientSession = 0
) {
  if (!turnsByOrder || !isFormattedFinalTurn(message)) return false;

  turnsByOrder.set(`${clientSession}:${message.turn_order}`, {
    ...message,
    clientSession,
  });
  return true;
}

// A final re-transcribes the whole turn, so it may legitimately be shorter
// than the last interim the user watched (entity formatting: "twenty five
// thousand dollars" -> "$25,000"). Losing more than half of a substantial
// transcript is not formatting — treat it as a truncated final and keep the
// longer text. Interims under the floor are too short for the ratio to be
// meaningful (entity compression alone can halve them).
export const TRANSCRIPT_SHRINK_GUARD_MIN_CHARS = 40;
export const TRANSCRIPT_SHRINK_GUARD_RATIO = 0.5;

function normalizedTranscriptLength(value) {
  return typeof value === "string"
    ? value.replace(/\s+/g, " ").trim().length
    : 0;
}

export function isSuspiciousTranscriptShrink(previousText, nextText) {
  const previousLength = normalizedTranscriptLength(previousText);
  const nextLength = normalizedTranscriptLength(nextText);

  if (nextLength === 0) return previousLength > 0;
  if (previousLength < TRANSCRIPT_SHRINK_GUARD_MIN_CHARS) return false;
  return nextLength < previousLength * TRANSCRIPT_SHRINK_GUARD_RATIO;
}

// A deliberate teardown mid-recording (proactive token refresh, browser-resume
// retry) destroys the in-progress turn: local state is cleared and the server
// never finalizes it. Stashing the interim snapshot with the collected finals
// keeps those words in the eventual submission.
export function preserveInterimFinalTurn(
  turnsByOrder,
  { clientSession = 0, turnOrder, transcript } = {}
) {
  if (!turnsByOrder) return false;

  const text = typeof transcript === "string" ? transcript.trim() : "";
  if (!text || !Number.isInteger(turnOrder)) return false;

  const key = `${clientSession}:${turnOrder}`;
  const existing = turnsByOrder.get(key);
  if (
    existing &&
    normalizedTranscriptLength(existing.transcript) >=
      normalizedTranscriptLength(text)
  ) {
    return false;
  }

  turnsByOrder.set(key, {
    type: "Turn",
    turn_order: turnOrder,
    end_of_turn: false,
    turn_is_formatted: false,
    transcript: text,
    clientSession,
    preserved_interim: true,
  });
  return true;
}

export function getOrderedFinalTurns(turnsByOrder) {
  return [...(turnsByOrder?.values?.() || [])].sort(
    (left, right) =>
      (left.clientSession ?? 0) - (right.clientSession ?? 0) ||
      left.turn_order - right.turn_order
  );
}

export function sendManualSessionBoundary(websocket) {
  websocket.send(JSON.stringify({ type: "ForceEndpoint" }));
  websocket.send(JSON.stringify({ type: "Terminate" }));
}

export function copyAudioBufferView(view) {
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
}

export function isManualEndpointConfirmed(endpointResult) {
  return Boolean(
    endpointResult?.status === "finalized" &&
      endpointResult?.sessionTerminated === true
  );
}

export function buildManualTurnTranscript({
  turns = [],
  finalizedTurns = [],
  fallbackTurn = null,
}) {
  const turnsByOrder = new Map();

  const addTurn = (turn, fallbackKey) => {
    const transcript =
      typeof turn?.transcript === "string" ? turn.transcript.trim() : "";
    if (!transcript) return;

    const turnOrder = Number.isInteger(turn?.turnOrder)
      ? turn.turnOrder
      : Number.isInteger(turn?.turn_order)
        ? turn.turn_order
        : null;
    const clientSession = Number.isInteger(turn?.clientSession)
      ? turn.clientSession
      : 0;
    const key = turnOrder == null
      ? fallbackKey
      : `${clientSession}:${turnOrder}`;

    // Later layers are more authoritative for the same turn — unless the
    // replacement loses most of the text the user already saw. A truncated
    // forced final must never erase a longer interim snapshot.
    const existing = turnsByOrder.get(key);
    if (
      existing &&
      isSuspiciousTranscriptShrink(existing.transcript, transcript)
    ) {
      return;
    }

    turnsByOrder.set(key, {
      clientSession,
      turnOrder: turnOrder ?? Number.POSITIVE_INFINITY,
      transcript,
    });
  };

  // Merge every source instead of letting the finalized set fully replace the
  // accumulated turns: when the finalize flow is cut short (termination
  // timeout, socket loss, token rotation), turns the user watched themselves
  // dictate can be missing from the finals and must survive from the interim
  // layers. Ordering: fallback snapshot < accumulated turns < finalized turns.
  addTurn(fallbackTurn, "active");
  turns.forEach((turn, index) => addTurn(turn, `turn-${index}`));
  finalizedTurns.forEach((turn, index) => addTurn(turn, `final-${index}`));

  return [...turnsByOrder.values()]
    .sort(
      (left, right) =>
        left.clientSession - right.clientSession ||
        left.turnOrder - right.turnOrder
    )
    .map((turn) => turn.transcript)
    .join(" ");
}

// Last-line safeguard for manual submissions: the transcript that gets sent
// must never be a drastically shrunken version of what the user watched
// themselves dictate. When the assembled result loses most of the interim
// snapshot (or is empty while the interim is not), submit the interim instead
// and let the caller log the substitution.
export function resolveSubmittedTranscript({
  builtTranscript,
  interimTranscript,
}) {
  const built =
    typeof builtTranscript === "string" ? builtTranscript.trim() : "";
  const interim =
    typeof interimTranscript === "string" ? interimTranscript.trim() : "";

  const usedInterimFallback =
    Boolean(interim) && isSuspiciousTranscriptShrink(interim, built);

  return {
    transcript: usedInterimFallback ? interim : built,
    usedInterimFallback,
    builtLength: built.length,
    interimLength: interim.length,
  };
}

export async function stopManualTranscriptionTurn({
  stopMicrophone,
  waitForQueueEmpty,
  clearQueuedAudio,
  finalizeSession,
}) {
  let stopError = null;
  let queueError = null;

  try {
    await stopMicrophone({ clearQueue: false });
  } catch (error) {
    stopError = error;
  }

  let queueDrained = false;
  if (!stopError) {
    try {
      queueDrained = await waitForQueueEmpty();
    } catch (error) {
      queueError = error;
    }
  }

  if (!queueDrained) {
    try {
      await clearQueuedAudio();
    } catch (error) {
      queueError ||= error;
    }

    if (!stopError && !queueError) {
      try {
        await waitForQueueEmpty();
      } catch (error) {
        queueError = error;
      }
    }
  }

  let endpointResult;
  let endpointError = null;

  try {
    endpointResult = await finalizeSession();
  } catch (error) {
    endpointError = error;
    endpointResult = { status: "unavailable", turns: [] };
  }

  return {
    endpointResult,
    queueDrained,
    stopError,
    queueError,
    endpointError,
  };
}
